import { Component, EventEmitter, Input, OnInit, Output, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';
import { AgentPromptService } from '../../agent-prompt.service';
import { AgentPromptVersion } from '../../agent-prompt.models';
import { PromptDiffComponent } from '../prompt-diff/prompt-diff.component';

/**
 * The History tab of the prompt editor: every saved version of one prompt,
 * newest first, with its required "what changed" note. Versions are immutable —
 * no edit, no delete. Selecting one shows a side-by-side diff against the
 * CURRENT text; Restore writes the old snapshot as a NEW version (after a
 * confirm) and emits so the parent reloads.
 */
@Component({
  selector: 'app-prompt-history-panel',
  standalone: true,
  imports: [CommonModule, PromptDiffComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './prompt-history-panel.component.html',
})
export class PromptHistoryPanelComponent implements OnInit {
  @Input({ required: true }) promptId!: number;

  /** A restore moved the head forward — parents reload the prompt + list. */
  @Output() restored = new EventEmitter<void>();

  private promptService = inject(AgentPromptService);

  versions = signal<AgentPromptVersion[]>([]);
  currentVersion = signal(0);
  loading = signal(false);
  errorMsg = signal('');

  // Diff state: the selected old version vs the current head text.
  selectedVersion = signal<AgentPromptVersion | null>(null);
  currentText = signal('');
  loadingDiff = signal(false);

  // Restore confirm state.
  restoreTarget = signal<AgentPromptVersion | null>(null);
  restoring = signal(false);

  ngOnInit(): void {
    this.loadVersions();
  }

  loadVersions(): void {
    this.loading.set(true);
    this.errorMsg.set('');
    this.promptService.listVersions(this.promptId).subscribe({
      next: (res) => {
        this.versions.set(res.versions ?? []);
        this.currentVersion.set(res.currentVersion ?? 0);
        this.loading.set(false);
      },
      error: (err) => {
        this.errorMsg.set(err?.message ?? 'Failed to load version history.');
        this.loading.set(false);
      },
    });
  }

  /** Load the old snapshot AND the current text, then show the diff. */
  viewDiff(version: AgentPromptVersion): void {
    if (this.selectedVersion()?.version === version.version) {
      this.selectedVersion.set(null); // toggle off
      return;
    }
    this.loadingDiff.set(true);
    this.errorMsg.set('');
    forkJoin({
      old: this.promptService.getVersion(this.promptId, version.version),
      current: this.promptService.getPrompt(this.promptId),
    }).subscribe({
      next: ({ old, current }) => {
        this.selectedVersion.set(old.version);
        this.currentText.set(current.prompt.systemPrompt ?? '');
        this.loadingDiff.set(false);
      },
      error: (err) => {
        this.errorMsg.set(err?.message ?? 'Could not load the versions to compare.');
        this.loadingDiff.set(false);
      },
    });
  }

  confirmRestore(version: AgentPromptVersion): void {
    this.restoreTarget.set(version);
  }

  cancelRestore(): void {
    this.restoreTarget.set(null);
  }

  executeRestore(): void {
    const target = this.restoreTarget();
    if (!target) return;
    this.restoring.set(true);
    this.promptService.restoreVersion(this.promptId, target.version).subscribe({
      next: () => {
        this.restoring.set(false);
        this.restoreTarget.set(null);
        this.selectedVersion.set(null);
        this.loadVersions();
        this.restored.emit();
      },
      error: (err) => {
        this.restoring.set(false);
        this.restoreTarget.set(null);
        this.errorMsg.set(err?.message ?? 'Restore failed.');
      },
    });
  }

  trackByVersion(_: number, version: AgentPromptVersion): number {
    return version.version;
  }
}
